import React, { useCallback, useRef, useState } from 'react';
import Button from '../../components/Button';
import LoadingIndicator from '../../components/LoadingIndicator';
import SettingCard from '../../settings/SettingCard';
import UIActions from '../../ui/UIActions';
import CreditCardInput from './CreditCardInput';
import styles from './EditCard.module.scss';
import { useAppDispatch } from '../../app/redux/useAppSelector';

interface EditCardModalProps {
  onClose: () => void;
  updateBilling: (params: { source: string }) => Promise<unknown>;
}

const EditCardModal = ({ onClose, updateBilling }: EditCardModalProps) => {
  const dispatch = useAppDispatch();

  const [card, setCard] =
    useState<stripe.elements.ElementChangeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getStripeTokenRef = useRef<
    (() => Promise<stripe.TokenResponse>) | undefined
  >();

  const onBindStripeTokenRef = useCallback(
    (cb?: () => Promise<stripe.TokenResponse>) => {
      getStripeTokenRef.current = cb;
    },
    [],
  );

  const handleSubmit = () => {
    setIsLoading(true);

    getStripeTokenRef
      .current?.()
      .then(({ token, error }) => {
        if (error || !token) {
          setIsLoading(false);
          return dispatch(
            UIActions.pushToast({
              level: 'error',
              message: 'Could not verify payment source',
            }),
          );
        }
        return updateBilling({ source: token.id });
      })
      .finally(onClose);
  };

  const isDisabled = Boolean(isLoading || !card?.complete || card.error);

  return (
    <SettingCard
      description={
        <>
          <div className={styles.details}>
            Entering a new credit card will replace any previous payment
            methods.
          </div>
          <CreditCardInput
            onBind={onBindStripeTokenRef}
            // @ts-expect-error
            onChange={setCard}
          />
          <div className={styles.buttons}>
            <Button
              variant="primary"
              disabled={isDisabled}
              onClick={handleSubmit}
            >
              {isLoading ? <LoadingIndicator delay={0} adaptColor /> : 'Update'}
            </Button>
            <Button
              className={styles.cancelButton}
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </>
      }
      title="New payment method"
    />
  );
};

export default EditCardModal;
